import { apiClient } from '@/lib/api/client'
import type { Task, TaskStatus, TaskType, TaskArtifact } from '@/types/entities'

export interface TaskListParams {
  status?: TaskStatus
  type?: TaskType
  limit?: number
  offset?: number
}

export interface TaskListResponse {
  data: Task[]
  count: number
  limit: number
  offset: number
}

export const taskApi = {
  // List all tasks with optional filtering
  getTasks: async (params: TaskListParams = {}): Promise<TaskListResponse> => {
    const response = await apiClient.get<TaskListResponse>('/api/tasks', {
      params: {
        status: params.status,
        type: params.type,
        limit: params.limit || 50,
        offset: params.offset || 0,
      },
    })
    return response.data
  },

  // Get active tasks (PENDING or RUNNING)
  getActiveTasks: async (limit = 50, offset = 0): Promise<TaskListResponse> => {
    const response = await apiClient.get<TaskListResponse>('/api/tasks/active', {
      params: { limit, offset },
    })
    return response.data
  },

  // Get single task with steps
  getTask: async (taskId: string): Promise<Task> => {
    const response = await apiClient.get<Task>(`/api/tasks/${taskId}`)
    return response.data
  },

  // Cancel a pending or running task
  cancelTask: async (taskId: string, force = false): Promise<Task> => {
    const url = force ? `/api/tasks/${taskId}/cancel?force=true` : `/api/tasks/${taskId}/cancel`
    const response = await apiClient.post<Task>(url, {})
    return response.data
  },

  // Retry a failed task
  retryTask: async (taskId: string): Promise<Task> => {
    const response = await apiClient.post<Task>(`/api/tasks/${taskId}/retry`)
    return response.data
  },

  // Get steps for a task
  getTaskSteps: async (taskId: string) => {
    const response = await apiClient.get(`/api/tasks/${taskId}/steps`)
    return response.data
  },

  // Get artifacts for a task
  getTaskArtifacts: async (taskId: string): Promise<{ data: TaskArtifact[]; count: number }> => {
    const response = await apiClient.get<{ data: TaskArtifact[]; count: number }>(`/api/tasks/${taskId}/artifacts`)
    return response.data
  },

  // Get task with full details (steps + artifacts)
  getTaskDetails: async (taskId: string): Promise<Task & { artifacts: TaskArtifact[] }> => {
    const response = await apiClient.get<Task & { artifacts: TaskArtifact[] }>(`/api/tasks/${taskId}/details`)
    return response.data
  },

  // Get artifact preview image URL
  getArtifactPreviewUrl: (taskId: string, artifactId: string): string => {
    return `/artwork-ms/api/tasks/${taskId}/artifacts/${artifactId}/preview`
  },
}
